import {
  DATE_FORMAT_YMD,
  DATE_TIME_FORMAT,
  EMPTY_STRING,
  FORMAT_ABBR_MONTH_DAY_YEAR,
  FORMAT_ABBR_MONTH_DAY_YEAR_TIME,
  FORMAT_LOCALIZED_MONTH_DAY_YEAR_TIME,
  FORMAT_ABBR_MONTH_YEAR,
  FORMAT_ABBR_YEAR,
  LOCAL_DATE_TIME,
  LOCAL_TIME_PATTERN,
  SHORT_DAY_NAME,
  TIME_FORMAT_24H,
  TIME_ONLY_FORMAT,
  TIME_UNIT_CONVERT_FACTOR
} from 'constants/general';
import { LOCALIZED_DURATION_FORMAT } from 'constants/time-duration';
import { MAX_DUE_DAYS_AHEAD } from 'constants/wheel';
import { DurationDisplayKind, MOMENT_DURATION_UNIT } from 'enums';
import {
  DurationDisplayParts,
  GetFutureDateTimeArgs,
  GetFutureFromNowArgs
} from 'interfaces/common.interface';
import moment, { Moment } from 'moment';
import 'moment-timezone';
import 'moment/min/locales';
import i18n, { t } from 'services/i18n.service';
import { getDurationFormat } from 'utils/helpers';

interface LocalizedDurationParts {
  readonly hours: number;
  readonly minutes: number;
}

interface DurationFormatLike {
  format: (duration: Record<string, number>) => string;
}

type DurationFormatConstructor = new (
  locales?: string | string[],
  options?: { style?: string }
) => DurationFormatLike;

const getDurationFormatConstructor = (): DurationFormatConstructor | null => {
  const intlWithDuration = Intl as typeof Intl & {
    DurationFormat?: DurationFormatConstructor;
  };
  return intlWithDuration.DurationFormat ?? null;
};

const formatCompactDurationFallback = ({
  hours,
  minutes
}: LocalizedDurationParts): string => {
  if (hours > 0 && minutes > 0) {
    return `${hours}h ${minutes}m`;
  }
  if (hours > 0) {
    return `${hours}h`;
  }
  return `${minutes}m`;
};

const toDurationFormatInput = ({
  hours,
  minutes
}: LocalizedDurationParts): Record<string, number> => {
  if (hours > 0 && minutes > 0) {
    return { hours, minutes };
  }
  if (hours > 0) {
    return { hours };
  }
  return { minutes };
};

export const formatLocalizedDuration = (
  parts: LocalizedDurationParts,
  locale: string = i18n.language
): string => {
  const normalized: LocalizedDurationParts = {
    hours: Math.max(0, parts.hours),
    minutes: Math.max(0, parts.minutes)
  };
  const DurationFormat = getDurationFormatConstructor();
  if (!DurationFormat) {
    return formatCompactDurationFallback(normalized);
  }

  try {
    const formatted = new DurationFormat(
      locale,
      LOCALIZED_DURATION_FORMAT.DEFAULT
    ).format(toDurationFormatInput(normalized));
    // DurationFormat omits all-zero units under display "auto".
    return formatted || formatCompactDurationFallback(normalized);
  } catch {
    return formatCompactDurationFallback(normalized);
  }
};

/**
 * Compact "3h 30m" label for a task's effort estimate. The todos API sends
 * `duration` (seconds) as a numeric string, so both shapes are accepted.
 */
export const formatTaskDurationLabel = (
  durationSeconds?: number | string
): string => {
  const seconds = Number(durationSeconds);
  if (!Number.isFinite(seconds) || seconds <= 0) {
    return EMPTY_STRING;
  }
  const totalMinutes = Math.round(
    seconds / TIME_UNIT_CONVERT_FACTOR.ONE_MINUTE_AS_SECONDS
  );
  if (totalMinutes <= 0) {
    return EMPTY_STRING;
  }
  const hours = Math.floor(
    totalMinutes / TIME_UNIT_CONVERT_FACTOR.ONE_HOUR_AS_MINUTES
  );
  const minutes = totalMinutes % TIME_UNIT_CONVERT_FACTOR.ONE_HOUR_AS_MINUTES;
  return formatLocalizedDuration({ hours, minutes });
};

/**
 * Pure duration bucketing for i18n labels (no translation).
 * Sub-minute → seconds; under an hour → rounded minutes; else hours ± minutes.
 */
export const getDurationDisplayParts = (
  durationSeconds: number
): DurationDisplayParts | null => {
  if (durationSeconds <= 0) {
    return null;
  }
  const secondsPerMinute = TIME_UNIT_CONVERT_FACTOR.ONE_MINUTE_AS_SECONDS;
  const minutesPerHour = TIME_UNIT_CONVERT_FACTOR.ONE_HOUR_AS_MINUTES;
  if (durationSeconds < secondsPerMinute) {
    return { kind: DurationDisplayKind.SECONDS, count: durationSeconds };
  }
  const totalMinutes = Math.round(durationSeconds / secondsPerMinute);
  if (totalMinutes < minutesPerHour) {
    return { kind: DurationDisplayKind.MINUTES, count: totalMinutes };
  }
  const hours = Math.floor(totalMinutes / minutesPerHour);
  const minutes = totalMinutes % minutesPerHour;
  if (minutes === 0) {
    return { kind: DurationDisplayKind.HOURS, count: hours };
  }
  return {
    kind: DurationDisplayKind.HOURS_MINUTES,
    hours,
    minutes
  };
};

export function formatAbbrMonthDayYear(dateString?: string | null) {
  if (!moment(dateString).isValid()) {
    return EMPTY_STRING;
  }
  return moment(dateString).format(FORMAT_ABBR_MONTH_DAY_YEAR);
}

export function formatShortDayName(
  date?: string | Date | Moment | null,
  language?: string
): string {
  if (!date || !moment(date).isValid()) {
    return EMPTY_STRING;
  }
  return moment(date)
    .locale(language || 'en')
    .format(SHORT_DAY_NAME);
}

export const formatToYMD = (date: string | Date | Moment) => {
  return moment(date).format(DATE_FORMAT_YMD);
};

export const getFutureDateTime = ({
  amount,
  unit = MOMENT_DURATION_UNIT.DAY,
  format = DATE_FORMAT_YMD,
  clampToTodayEnd = false,
  endOfDayTime = LOCAL_DATE_TIME.END_OF_DAY_TIME
}: GetFutureDateTimeArgs): string => {
  const finish = moment().add(amount, unit).seconds(0).milliseconds(0);
  if (clampToTodayEnd && !finish.isSame(moment(), 'day')) {
    return endOfDayTime;
  }
  return finish.format(format);
};

export const getFutureDate = (daysToAdd: number) =>
  getFutureDateTime({ amount: daysToAdd });

export function getFutureFromNow(
  args: GetFutureFromNowArgs & { format: string }
): string;
export function getFutureFromNow(args: GetFutureFromNowArgs): Date;
export function getFutureFromNow({
  amount,
  unit = MOMENT_DURATION_UNIT.MINUTE,
  format
}: GetFutureFromNowArgs): Date | string {
  const result = moment()
    .add(amount, unit)
    .seconds(0)
    .milliseconds(0);

  return format != null ? result.format(format) : result.toDate();
}

export const parseDurationMinutes = (value: string): number | null => {
  if (value.trim().length === 0) {
    return null;
  }
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    return null;
  }
  const rounded = Math.round(parsed);
  return rounded > 0 ? rounded : null;
};

export const getTomorrowISODate = () => {
  return getFutureDate(1);
};

export const getTomorrowMinDateTimeLocal = () => {
  return moment().add(1, 'day').startOf('day').format(LOCAL_DATE_TIME.FORMAT);
};

export const getDateTimeLocalMinutesFromNow = (
  minutesFromNow: number
): string =>
  getFutureDateTime({
    amount: minutesFromNow,
    unit: MOMENT_DURATION_UNIT.MINUTE,
    format: LOCAL_DATE_TIME.FORMAT
  });

export function formatAbbrMonthDayYearTime(dateString?: string | null): string {
  // moment(undefined) is a valid "now", so a missing date would render as the current time.
  if (!dateString || !moment(dateString).isValid()) {
    return EMPTY_STRING;
  }
  return moment(dateString).format(FORMAT_ABBR_MONTH_DAY_YEAR_TIME);
}

export function formatLocalizedMonthDayYearTime(
  dateString?: string | null,
  language?: string,
  emptyFallback = EMPTY_STRING
): string {
  if (!dateString || !moment(dateString).isValid()) {
    return emptyFallback;
  }
  return moment(dateString)
    .locale(language || 'en')
    .format(FORMAT_LOCALIZED_MONTH_DAY_YEAR_TIME);
}

export const getCurrentISODate = () => moment().toISOString();

/** Local-offset ISO-ish stamp (`moment().format()`), not UTC `Z`. */
export const getNowLocalIso = (): string => moment().format();

export const getEndOfDay = (value: moment.MomentInput) =>
  moment(value).endOf('day').format();

export const diffInSeconds = (
  time1?: string,
  time2?: string,
  format: string = TIME_FORMAT_24H
) => moment(time1, format).diff(moment(time2, format), 'seconds');

/** Forward duration from startTime to endTime on a 24h clock (wraps past midnight). */
export const getWrappedForwardDiffInSeconds = (
  endTime?: string,
  startTime?: string,
  format: string = TIME_FORMAT_24H
): number => {
  /** Seconds in one 24-hour clock cycle (e.g. wrapping shutdown → next-day curfew). */
  const SECONDS_PER_CLOCK_DAY = TIME_UNIT_CONVERT_FACTOR.ONE_DAY_AS_SECONDS;

  const rawDiff = diffInSeconds(endTime, startTime, format);

  return (rawDiff + SECONDS_PER_CLOCK_DAY) % SECONDS_PER_CLOCK_DAY;
};

export const formatTimeTo12Hour = (time?: string, seconds?: number) =>
  moment(time, TIME_FORMAT_24H).isValid()
    ? moment(time, TIME_FORMAT_24H)
        .add(seconds ?? 0, 'seconds')
        .format('hh:mm a')
    : EMPTY_STRING;

export const formatTimeTo12HourMinusSeconds = (
  time?: string,
  secondsToSubtract?: number
) =>
  moment(time, TIME_FORMAT_24H).isValid()
    ? moment(time, TIME_FORMAT_24H)
        .subtract(secondsToSubtract ?? 0, 'seconds')
        .format('hh:mm a')
    : EMPTY_STRING;

export const timeStringToSeconds = (time: string): number => {
  const parsed = moment(time, TIME_FORMAT_24H);
  if (!parsed.isValid()) {
    return 0;
  }

  const hours = parsed.hours();
  const minutes = parsed.minutes();

  return (
    hours * TIME_UNIT_CONVERT_FACTOR.ONE_HOUR_AS_SECONDS +
    minutes * TIME_UNIT_CONVERT_FACTOR.ONE_MINUTE_AS_SECONDS
  );
};

export const convertSecondsToWholeMinutes = (seconds: number): number => {
  return Math.floor(seconds / TIME_UNIT_CONVERT_FACTOR.ONE_MINUTE_AS_SECONDS);
};

export const addMinutesToTime = (time: string, minutes: number): string => {
  const parsed = moment(time, TIME_ONLY_FORMAT);
  if (!parsed.isValid()) {
    return '00:00';
  }

  return parsed.add(minutes, 'minutes').format(TIME_ONLY_FORMAT);
};

export function formatTo24(time12?: string) {
  if (!time12) {
    return undefined;
  }

  const parsed = moment(
    time12,
    ['hh:mm a', 'hh:mm A', 'HH:mm a', 'HH:mm A'],
    true
  );
  if (!parsed.isValid()) {
    return undefined;
  }

  return parsed.format(TIME_ONLY_FORMAT);
}

/** Formats epoch millis as `HH:mm` for TimeWheelPicker / timebox inputs. */
export const formatMillisToTimeOnly = (
  millis: number | null | undefined
): string | undefined => {
  if (millis == null || !Number.isFinite(millis)) {
    return undefined;
  }
  const parsed = moment(millis);
  if (!parsed.isValid()) {
    return undefined;
  }
  return parsed.format(TIME_ONLY_FORMAT);
};

export const getStartEndTimes12h = (durationSeconds?: number | null) => {
  const seconds =
    typeof durationSeconds === 'number' && Number.isFinite(durationSeconds)
      ? durationSeconds
      : 0;

  const startTime = moment().format('h:mm a');
  const endTime = moment().add(seconds, 'seconds').format('h:mm a');

  return { startTime, endTime };
};

export const formatSecondsAsMinutes = (seconds?: number | null) => {
  const safeSeconds =
    typeof seconds === 'number' && Number.isFinite(seconds) ? seconds : 0;
  return moment().startOf('day').add(safeSeconds, 'seconds').format('mm');
};

export const formatCountdownTimer = (seconds?: number | null) => {
  const safeSeconds =
    typeof seconds === 'number' && Number.isFinite(seconds) ? seconds : 0;
  const format =
    safeSeconds >= TIME_UNIT_CONVERT_FACTOR.ONE_HOUR_AS_SECONDS
      ? 'hh:mm:ss'
      : 'mm:ss';
  return moment().startOf('date').add(safeSeconds, 'seconds').format(format);
};

/**
 * Parse a time string that may be in either "h:mm a" or "h:mm:ss a" and return
 * a consistently formatted "h:mm a" label. Returns EMPTY_STRING when invalid.
 */
export const formatTo12HourLabel = (time?: string) => {
  if (!time) {
    return EMPTY_STRING;
  }

  const parsed = moment(time, ['h:mm a', 'h:mm:ss a'], true);
  if (!parsed.isValid()) {
    return EMPTY_STRING;
  }

  return parsed.format('h:mm a');
};

export const getTimeOnly = (dateTimeStr: string) =>
  moment(dateTimeStr, DATE_TIME_FORMAT).format(TIME_ONLY_FORMAT);

export const formatAbbrYear = (date: string | Date | Moment) => {
  return moment(date).format(FORMAT_ABBR_YEAR);
};

export const formatAbbrMonthYear = (date: string | Date | Moment) => {
  return moment(date).format(FORMAT_ABBR_MONTH_YEAR);
};

// Formats a concise "Start: 1:23 pm - End: 1:45 pm" label
export const formatStartEndRange = (
  startTime?: string,
  endTime?: string,
  startLabel?: string,
  endLabel?: string
) => {
  const start = formatTo12HourLabel(startTime);
  const end = formatTo12HourLabel(endTime);
  if (!start && !end) {
    return '';
  }
  return `${startLabel ?? 'Start'}: ${start} - ${endLabel ?? 'End'}: ${end}`;
};

export const formatOrdinalDayMonthYear = (dateString?: string | null) => {
  if (!moment(dateString).isValid()) {
    return EMPTY_STRING;
  }
  return moment(dateString).format('Do MMM YYYY');
};

/** Ordinal day + short month (e.g. "16th Jul"). */
export const formatOrdinalDayMonth = (
  date?: string | Date | Moment | null
): string => {
  if (!moment(date).isValid()) {
    return EMPTY_STRING;
  }
  return moment(date).format('Do MMM');
};

export const formatDueDate = (dateString?: string | null): string => {
  if (!moment(dateString).isValid()) {
    return EMPTY_STRING;
  }

  const date = moment(dateString);
  const today = moment().startOf('day');
  const tomorrow = moment().add(1, 'day').startOf('day');
  const yesterday = moment().subtract(1, 'day').startOf('day');
  const dateStart = date.clone().startOf('day');

  if (dateStart.isSame(today, 'day')) {
    return t('todos.today');
  }

  if (dateStart.isSame(tomorrow, 'day')) {
    return t('todos.tomorrow');
  }

  if (dateStart.isSame(yesterday, 'day')) {
    return t('todos.yesterday');
  }

  const currentYear = moment().year();
  if (date.year() === currentYear) {
    return date.format('Do MMM'); // "18th Jan"
  }

  return date.format('Do MMM YYYY'); // "18th Jan 2026"
};

export const formatDateToYMD = (date: string | Date | Moment): string => {
  return moment(date).format('YYYY-MM-DD');
};

/** Local calendar day key (`yyyy-MM-dd`) for a Date. */
export const formatDayKey = (date: Date): string => formatDateToYMD(date);

/** Today's day key in the user's local timezone. */
export const getTodayKey = (): string => formatDayKey(new Date());

export const isDayToday = (day: string): boolean => day === getTodayKey();

export const isDayTodayOrFuture = (day: string): boolean =>
  day >= getTodayKey();

export const isDayPast = (day: string): boolean => day < getTodayKey();

/** Add (or subtract) calendar days to a `YYYY-MM-DD` key. */
export const addDaysToYMD = (day: string, daysToAdd: number): string =>
  moment(day, DATE_FORMAT_YMD).add(daysToAdd, 'day').format(DATE_FORMAT_YMD);

/** Monday (`YYYY-MM-DD`) of the ISO week that contains `day`. */
export const getIsoWeekStartYMD = (day: string): string =>
  moment(day, DATE_FORMAT_YMD).startOf('isoWeek').format(DATE_FORMAT_YMD);

/** Consecutive `YYYY-MM-DD` keys starting at `startDay`. */
export const getYMDRangeKeys = (startDay: string, length: number): string[] =>
  Array.from({ length }, (_, index) => addDaysToYMD(startDay, index));

/** Day-of-month number as a string (e.g. `"16"`). */
export const formatDayOfMonth = (date: string | Date | Moment): string =>
  moment(date).format('D');

/** Compact calendar label (e.g. `"Wed, 16 Jul 2026"`). */
export const formatWeekdayDayMonthYear = (day: string): string =>
  moment(day, DATE_FORMAT_YMD).format('ddd, DD MMM YYYY');

export const formatDateToAbbrMonth = (date: string | Date | Moment): string => {
  return moment(date).format('MMM');
};

export const formatDateToAbbrMonthDayYear = (
  date: string | Date | Moment
): string => {
  return moment(date).format('MMM DD, YYYY');
};

export const formatRelativeTimeFromNow = (
  value?: string | Date | Moment | null
): string => {
  if (!value) {
    return EMPTY_STRING;
  }

  const m = moment(value);
  if (!m.isValid()) {
    return EMPTY_STRING;
  }

  return m.fromNow();
};

/** True when due date is before today (day-level; due today is not overdue). */
export const isDueDateOverdue = (
  dueDate?: string | Date | Moment | null
): boolean => {
  if (dueDate == null) {
    return false;
  }
  const due = moment(dueDate);
  if (!due.isValid()) {
    return false;
  }
  return due.isBefore(moment(), 'day');
};

/** True when due date falls on the local calendar day of today. */
export const isDueDateToday = (
  dueDate?: string | Date | Moment | null
): boolean => {
  if (dueDate == null) {
    return false;
  }
  const due = moment(dueDate);
  if (!due.isValid()) {
    return false;
  }
  return due.isSame(moment(), 'day');
};

export const formatSecondsAsMinutesSecondsLabel = (
  seconds?: number | null
): string => {
  const safeSeconds =
    typeof seconds === 'number' && Number.isFinite(seconds) ? seconds : 0;

  return moment()
    .startOf('date')
    .add(safeSeconds, 'seconds')
    .format('mm[m]:ss[s]');
};

export const formatDurationSeconds = (seconds: number): string =>
  moment()
    .startOf('date')
    .add(seconds, 'seconds')
    .format(getDurationFormat(seconds));

/** Localized compact "45s ago" / "3m ago" / "2h ago" label for an elapsed duration. */
export const formatElapsedAgo = (
  elapsedSeconds: number,
  locale: string = i18n.language
): string => {
  const formatter = new Intl.RelativeTimeFormat(locale, {
    numeric: 'always',
    style: 'narrow'
  });
  if (elapsedSeconds < TIME_UNIT_CONVERT_FACTOR.ONE_MINUTE_AS_SECONDS) {
    return formatter.format(-elapsedSeconds, MOMENT_DURATION_UNIT.SECOND);
  }
  if (elapsedSeconds < TIME_UNIT_CONVERT_FACTOR.ONE_HOUR_AS_SECONDS) {
    const minutes = Math.floor(
      elapsedSeconds / TIME_UNIT_CONVERT_FACTOR.ONE_MINUTE_AS_SECONDS
    );
    return formatter.format(-minutes, MOMENT_DURATION_UNIT.MINUTE);
  }
  const hours = Math.floor(
    elapsedSeconds / TIME_UNIT_CONVERT_FACTOR.ONE_HOUR_AS_SECONDS
  );
  return formatter.format(-hours, MOMENT_DURATION_UNIT.HOUR);
};

/** Parses `datetime-local` values (YYYY-MM-DDTHH:mm) as local wall-clock time. */
const DATE_TIME_LOCAL_PATTERN = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/;

export function parseDateTimeLocal(value: string): Date | null {
  const match = DATE_TIME_LOCAL_PATTERN.exec(value);
  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const hour = Number(match[4]);
  const minute = Number(match[5]);
  const date = new Date(year, month - 1, day, hour, minute, 0, 0);

  return Number.isNaN(date.getTime()) ? null : date;
}

export function dateTimeLocalToISOString(value: string): string | null {
  const date = parseDateTimeLocal(value);
  return date ? date.toISOString() : null;
}

export const DEFAULT_DATETIME_LOCAL_TIME = '00:00';

/** Splits a `datetime-local` value into its `YYYY-MM-DD` date and `HH:mm` time parts. */
export function splitDateTimeLocal(value: string): {
  date: string;
  time: string;
} {
  const match = DATE_TIME_LOCAL_PATTERN.exec(value);
  if (!match) return { date: EMPTY_STRING, time: EMPTY_STRING };
  const [, year, month, day, hour, minute] = match;
  return { date: `${year}-${month}-${day}`, time: `${hour}:${minute}` };
}

/** Combines a `YYYY-MM-DD` date and `HH:mm` time into a `datetime-local` string. */
export function combineDateTimeLocal(date: string, time: string): string {
  if (!date || !time) return EMPTY_STRING;
  return `${date}T${time}`;
}

/** Today's calendar date at a strict `HH:mm` local time, or null when invalid. */
export const parseTodayTimeLocal = (timeLocal: string): Date | null => {
  if (LOCAL_TIME_PATTERN.HM.test(timeLocal) !== true) {
    return null;
  }
  return parseDateTimeLocal(combineDateTimeLocal(getTodayKey(), timeLocal));
};

/** True when today's `HH:mm` is strictly after now (same-day, no next-day rollover). */
export const isTodayTimeLocalInFuture = (timeLocal: string): boolean => {
  const date = parseTodayTimeLocal(timeLocal);
  return date != null && date.getTime() > Date.now();
};

/**
 * Strictly parse a `YYYY-MM-DD` calendar date. Returns null when invalid.
 */
export const parseYmdMoment = (dateYmd: string): Moment | null => {
  const parsed = moment(dateYmd, DATE_FORMAT_YMD, true);
  if (parsed.isValid() !== true) {
    return null;
  }
  return parsed;
};

const MIDDAY_HOUR = 12;

/** YMD → ISO at local midday so the calendar day stays stable across timezones. */
export const ymdToMiddayIso = (dateYmd: string): string | null => {
  const parsed = parseYmdMoment(dateYmd);
  if (parsed == null) {
    return null;
  }
  return parsed.hour(MIDDAY_HOUR).minute(0).second(0).millisecond(0).toISOString();
};

/**
 * Combines `YYYY-MM-DD` + `HH:mm` or `HH:mm:ss` into `YYYY-MM-DDTHH:mm:ss`.
 * Returns null when the date or time is invalid.
 */
export const combineLocalDateTimeWithSeconds = (
  dateYmd: string,
  time: string
): string | null => {
  if (parseYmdMoment(dateYmd) == null) {
    return null;
  }
  let normalizedTime = time.trim();
  if (LOCAL_TIME_PATTERN.HM.test(normalizedTime) === true) {
    normalizedTime = `${normalizedTime}:00`;
  }
  if (LOCAL_TIME_PATTERN.HMS.test(normalizedTime) !== true) {
    return null;
  }
  return `${dateYmd}T${normalizedTime}`;
};

/**
 * True when `timezone` is a real IANA zone (matches BE `@IsTimeZone` / Luxon intent).
 * Rejects offset strings like `UTC+00:00` that user.details often stores.
 */
export const isValidIanaTimezone = (timezone: string): boolean =>
  moment.tz.zone(timezone) != null;

export const resolveBrowserIanaTimezone = (): string | null => {
  const browserZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  if (browserZone != null && isValidIanaTimezone(browserZone)) {
    return browserZone;
  }
  return null;
};

export const getDeclaredIanaTimezone = (
  userTimezone?: string | null
): string | null => {
  const trimmed =
    userTimezone != null && userTimezone.trim().length > 0
      ? userTimezone.trim()
      : null;
  if (trimmed != null && isValidIanaTimezone(trimmed)) {
    return trimmed;
  }
  return null;
};

/** Declared profile IANA, else device. Null when neither exists (no UTC fallback). */
export const resolveProfileOrDeviceIanaTimezone = (
  userTimezone?: string | null
): string | null =>
  getDeclaredIanaTimezone(userTimezone) ?? resolveBrowserIanaTimezone();

export const buildIanaTimezoneOptions = (): {
  label: string;
  value: string;
}[] => moment.tz.names().map((zone) => ({ label: zone, value: zone }));

/**
 * Returns the `YYYY-MM-DD` days within an inclusive `datetime-local` range, newest first.
 * Used to build a webview-safe day picker that replaces native date inputs.
 */
export function getDateRangeYMD(
  minDateTimeLocal: string,
  maxDateTimeLocal: string
): string[] {
  const min = parseDateTimeLocal(minDateTimeLocal);
  const max = parseDateTimeLocal(maxDateTimeLocal);
  if (!min || !max) return [];

  const days: string[] = [];
  const cursor = moment(max).startOf('day');
  const start = moment(min).startOf('day');

  while (cursor.isSameOrAfter(start, 'day')) {
    days.push(cursor.format(DATE_FORMAT_YMD));
    cursor.subtract(1, 'day');
  }

  return days;
}

/** Formats a true elapsed duration (supports spans longer than 24 hours). */
export const formatElapsedDurationSeconds = (seconds: number): string => {
  const duration = moment.duration(seconds, 'seconds');
  const hours = Math.floor(duration.asHours());
  const minutes = duration.minutes();
  const secs = duration.seconds();

  if (seconds >= TIME_UNIT_CONVERT_FACTOR.ONE_HOUR_AS_SECONDS) {
    return `${hours}h:${String(minutes).padStart(2, '0')}m:${String(secs).padStart(2, '0')}s`;
  }

  return `${minutes}m:${String(secs).padStart(2, '0')}s`;
};

export const humanizeDurationSeconds = (
  seconds: number,
  locale: string
): string => moment.duration(seconds, 'seconds').locale(locale).humanize();

export const toDateTimeLocalString = (date: Date | string): string => {
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return '';
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
};

export function isFutureDateTime(value?: string | Date | null): boolean {
  if (!value) return false;

  const m = moment(value);
  if (!m.isValid()) return false;

  return m.isAfter(moment());
}

export const isDueWithinNextDays = (
  dueDate: string,
  days: number = MAX_DUE_DAYS_AHEAD
): boolean => {
  if (!dueDate) return false;
  const due = moment(dueDate);
  const now = moment().startOf('day');
  const maxDate = moment().add(days, 'days').endOf('day');
  return due.isSameOrAfter(now) && due.isSameOrBefore(maxDate);
};

export function isNowOrAfter(
  value?: string | Date | null,
  unit: moment.unitOfTime.StartOf = 'second'
): boolean {
  if (!value) return false;

  const momentValue = moment(value);
  if (!momentValue.isValid()) return false;

  return momentValue.isSameOrAfter(moment(), unit);
}

export function isTodayOnOrBefore(value?: string | Date | null): boolean {
  if (!value) return false;

  const momentValue = moment(value);
  if (!momentValue.isValid()) return false;

  return moment().isSameOrBefore(momentValue, 'day');
}

export function getDaysUntilDate(value?: string | Date | null): number | null {
  if (!value) return null;

  const momentValue = moment(value);
  if (!momentValue.isValid()) return null;

  return Math.max(momentValue.diff(moment(), 'days'), 0);
}
